import { Author } from '../../newsletter/types/Newsletter';
import Dialog from '../../common/components/Dialog';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import AuthorAndTopicCard from './AuthorAndTopicCard';
import { useTranslations } from '../../localization/useTranslations';

type AuthorAndTopicDialogProps = {
  authorOrTopic: Author[] | null;
  type: 'author' | 'topic';
  isOpen: boolean;
  onClose: () => void;
  onFollowClick: (id: number, type: 'author' | 'topic') => void;
};

const AuthorAndTopicDialog = ({ authorOrTopic, type, isOpen, onClose, onFollowClick }: AuthorAndTopicDialogProps) => {
  const [filteredAuthorsOrTopics, setFilteredAuthorsOrTopics] = useState<Author[] | null>(authorOrTopic);
  const [searchValue, setSearchValue] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const filteredTopics: any = authorOrTopic?.filter((topic) =>
      topic.name.toLowerCase().includes(value.toLowerCase())
    );

    setFilteredAuthorsOrTopics(filteredTopics);
    const inputValue = e.target.value;
    setSearchValue(inputValue);
  };

  const handleInputClear = () => {
    setSearchValue('');
    setFilteredAuthorsOrTopics(authorOrTopic);
  };

  const translations = useTranslations();

  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setFilteredAuthorsOrTopics(authorOrTopic);
  }, [authorOrTopic]);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <div
        id={`follow-search-modal-${type}`}
        className={`follow-search-modal follow-search-modal--${type} modal modal--large`}
        ref={dialogRef}
      >
        <div className="follow-search-modal__wrapper modal__wrapper">
          <div className="follow-search-modal__header modal__header">
            <h5 className="follow-search-modal__title archivo">
              {type === 'author'
                ? translations?.authorAndTopic?.followAuthor || 'Follow author'
                : translations?.authorAndTopic?.followTopic || 'Follow topic'}
            </h5>

            <button className="close-button" onClick={onClose} />
          </div>

          <div className="follow-search-modal__content modal__content">
            <div className="follow-search-modal__search">
              <div className="follow-search-modal__search-input-wrapper">
                <input
                  id={`follow-search-modal__search-input-${type}`}
                  className="follow-search-modal__search-input basic basic-16"
                  type="text"
                  placeholder={
                    type === 'author'
                      ? translations?.authorAndTopic?.searchAuthor || 'Search author'
                      : translations?.authorAndTopic?.searchTopic || 'Search topic'
                  }
                  value={searchValue}
                  onChange={(e) => handleInputChange(e)}
                />
                {searchValue && (
                  <button className="clear-button" onClick={handleInputClear}>
                    {translations?.authorAndTopic?.clearButton || 'Clear'}
                  </button>
                )}
              </div>
            </div>

            <div className="follow-search-modal__search-results">
              {filteredAuthorsOrTopics &&
                filteredAuthorsOrTopics.map((author: Author) => (
                  <AuthorAndTopicCard
                    key={author.id}
                    title={author.name}
                    id={author.id.toString()}
                    type={type}
                    name={author.name}
                    shortDescription={author.acf?.newsletter_description ?? author.acf?.shortDescription ?? ''}
                    link={author.link}
                    isFollowed={false}
                    searchModalOpen={true}
                    imageUrl={author.acf?.newsletter_image ?? author.acf?.profileImageUrl ?? ''}
                    onFollowClick={() => {
                      onFollowClick(author.id, type);
                    }}
                    layout={'full-width'}
                    isFollowable={true}
                    nameType="link" // in search modal, clicking on name should redirect to author page instead opening the author modal
                  />
                ))}
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default AuthorAndTopicDialog;
